/* eslint-disable complexity */
/**
 * @file 管理平台下线迁移提示弹窗
 * @author sunyingying01
 */

import React, {useCallback, useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {Button, Modal} from 'antd';
import {ExclamationCircleFilled, InfoCircleFilled} from '@ant-design/icons';
import {useLocation} from 'react-router-dom';
import cx from 'classnames';

import CloseSvg from '@assets/icons/close.svg';
import {UserStore} from '@/stores';
import {
    MigrationPhase,
    goToNewPlatform,
    isNoticeModalClosed,
    isRestrictedModalShown,
    markNoticeModalClosed,
    markRestrictedModalShown
} from '@/utils/migration';
import {
    GO_TO_NEW_PLATFORM,
    NOTICE_CONTENT,
    NOTICE_TITLE,
    RESTRICTED_CONTENT,
    getRestrictedTitle
} from '../constants';
import styles from './index.module.less';

interface MigrationModalProps {
    userStore: UserStore;
}

/** 延迟弹出，等首屏任务数据加载完再展示，避免盖在全屏 loading 上 */
const OPEN_DELAY_MS = 800;

const MigrationModal: React.FC<MigrationModalProps> = observer(({userStore}) => {
    const {migrationPhase, userId, workspaceOfflineDays} = userStore;
    const {pathname} = useLocation();
    const [visible, setVisible] = useState(false);
    const isEnterpriseAdminRoute = pathname === '/admin' || pathname.startsWith('/admin/');

    // 轻量通知期：每个用户仅弹 1 次（localStorage）
    // 受限运行期：同一标签页会话内仅弹 1 次（sessionStorage）
    // 标记在关闭时写入，避免弹窗被上层重新挂载时「弹过但没看到」
    useEffect(
        () => {
            if (isEnterpriseAdminRoute) {
                setVisible(false);
                return undefined;
            }

            const shouldOpen = (migrationPhase === MigrationPhase.NOTICE && !!userId && !isNoticeModalClosed(userId))
                || (migrationPhase === MigrationPhase.RESTRICTED && !isRestrictedModalShown());

            if (!shouldOpen) {
                return undefined;
            }

            const timer = setTimeout(() => setVisible(true), OPEN_DELAY_MS);
            return () => clearTimeout(timer);
        },
        [migrationPhase, userId, isEnterpriseAdminRoute]
    );

    const handleClose = useCallback(
        () => {
            if (migrationPhase === MigrationPhase.NOTICE) {
                markNoticeModalClosed(userId);
            } else if (migrationPhase === MigrationPhase.RESTRICTED) {
                markRestrictedModalShown();
            }
            setVisible(false);
        },
        [migrationPhase, userId]
    );

    const handleGoToNewPlatform = useCallback(
        () => {
            handleClose();
            goToNewPlatform();
        },
        [handleClose]
    );

    const isNotice = migrationPhase === MigrationPhase.NOTICE;

    if (isEnterpriseAdminRoute
        || (migrationPhase !== MigrationPhase.NOTICE && migrationPhase !== MigrationPhase.RESTRICTED)) {
        return null;
    }

    return (
        <Modal
            open={visible}
            centered
            width={400}
            footer={null}
            maskClosable={false}
            destroyOnHidden
            closable={isNotice}
            closeIcon={isNotice ? <CloseSvg /> : null}
            rootClassName={styles.migrationModal}
            onCancel={handleClose}
        >
            <div className={styles.header}>
                {isNotice
                    ? <InfoCircleFilled className={cx(styles.icon, styles.noticeIcon)} />
                    : <ExclamationCircleFilled className={cx(styles.icon, styles.restrictedIcon)} />}
                <span className={styles.title}>
                    {isNotice ? NOTICE_TITLE : getRestrictedTitle(workspaceOfflineDays ?? 0)}
                </span>
            </div>
            <div className={styles.content}>
                {isNotice ? NOTICE_CONTENT : RESTRICTED_CONTENT}
            </div>
            <div className={styles.footer}>
                <Button onClick={handleClose}>
                    {isNotice ? '我知道了' : '继续查看历史任务'}
                </Button>
                <Button type="primary" onClick={handleGoToNewPlatform}>
                    {GO_TO_NEW_PLATFORM}
                </Button>
            </div>
        </Modal>
    );
});

export default MigrationModal;
